import { supabase } from '@/integrations/supabase/client';
import { OnboardingData, OnboardingMeta, CompanySize, ReferralSource, OnboardingProfile } from '@/types/onboarding.types';

export interface OnboardingServiceResponse<T = any> {
  data: T | null;
  error: Error | null;
}

export interface UpdateOnboardingPayload {
  referral_source?: ReferralSource;
  onboarding_profile?: OnboardingProfile;
  onboarding_meta: OnboardingMeta;
}

// Error types for better error handling
export class OnboardingError extends Error {
  constructor(
    message: string,
    public code: string,
    public retryable: boolean = false
  ) {
    super(message);
    this.name = 'OnboardingError';
  }
}

export class NetworkError extends OnboardingError {
  constructor(message: string) {
    super(message, 'NETWORK_ERROR', true);
    this.name = 'NetworkError';
  }
}

export class ValidationError extends OnboardingError {
  constructor(message: string) {
    super(message, 'VALIDATION_ERROR', false);
    this.name = 'ValidationError';
  }
}

// Retry configuration
const RETRY_CONFIG = {
  maxAttempts: 3,
  baseDelay: 1000, // 1 second
  maxDelay: 5000,  // 5 seconds
};

// Utility function for exponential backoff retry
async function withRetry<T>(
  operation: () => Promise<T>,
  maxAttempts: number = RETRY_CONFIG.maxAttempts
): Promise<T> {
  let lastError: Error;
  
  for (let attempt = 1; attempt <= maxAttempts; attempt++) {
    try {
      return await operation();
    } catch (error) {
      lastError = error as Error;
      
      // Don't retry on validation errors or non-retryable errors
      if (error instanceof ValidationError || 
          (error instanceof OnboardingError && !error.retryable)) {
        throw error;
      }
      
      // Don't retry on the last attempt
      if (attempt === maxAttempts) {
        break;
      }
      
      // Calculate delay with exponential backoff
      const delay = Math.min(
        RETRY_CONFIG.baseDelay * Math.pow(2, attempt - 1),
        RETRY_CONFIG.maxDelay
      );
      
      console.warn(`Onboarding operation failed (attempt ${attempt}/${maxAttempts}), retrying in ${delay}ms:`, error);
      await new Promise(resolve => setTimeout(resolve, delay));
    }
  }
  
  throw lastError!;
}

// Enhanced error handling for Supabase operations
function handleSupabaseError(error: any, operation: string): never {
  console.error(`Supabase error in ${operation}:`, error);
  
  // Network/connection errors
  if (error.message?.includes('fetch') || 
      error.message?.includes('network') ||
      error.message?.includes('timeout')) {
    throw new NetworkError(`Network error during ${operation}. Please check your connection and try again.`);
  }
  
  // Permission/auth errors
  if (error.code === 'PGRST301' || error.message?.includes('permission')) {
    throw new OnboardingError(
      `Permission denied during ${operation}. Please refresh the page and try again.`,
      'PERMISSION_ERROR',
      false
    );
  }
  
  // Validation errors
  if (error.code?.startsWith('23') || error.message?.includes('constraint')) {
    throw new ValidationError(`Invalid data provided for ${operation}. Please check your inputs.`);
  }
  
  // Generic database error
  throw new OnboardingError(
    `Database error during ${operation}: ${error.message}`,
    'DATABASE_ERROR',
    true
  );
}

export const onboardingService = {
  /**
   * Update onboarding data for an organization
   */
  async updateOnboardingData(
    organizationId: string, 
    stepData: Partial<OnboardingData>,
    currentStep: number,
    totalSteps: number,
    advanceStep: boolean = false
  ): Promise<OnboardingServiceResponse<OnboardingData>> {
    try {
      // Input validation
      if (!organizationId) {
        throw new ValidationError('Organization ID is required');
      }
      
      if (currentStep < 1 || currentStep > totalSteps) {
        throw new ValidationError(`Invalid step: ${currentStep}. Must be between 1 and ${totalSteps}`);
      }

      const result = await withRetry(async () => {
        // Calculate the step to save (current step or next step if advancing)
        const stepToSave = advanceStep && currentStep < totalSteps ? currentStep + 1 : currentStep;
        
        // Build the updated meta
        const updatedMeta: OnboardingMeta = {
          completed: false,
          current_step: stepToSave,
          completed_steps: Array.from(new Set([
            ...(stepData.onboarding_meta?.completed_steps || []),
            ...(advanceStep ? [currentStep] : [])
          ]))
        };

        // Prepare the update payload with proper typing
        const updatePayload: UpdateOnboardingPayload = {
          onboarding_meta: updatedMeta
        };

        // Only include fields that are being updated
        // Company size step removed from onboarding flow
        if (stepData.referral_source !== undefined) {
          updatePayload.referral_source = stepData.referral_source;
        }
        if (stepData.onboarding_profile !== undefined) {
          updatePayload.onboarding_profile = stepData.onboarding_profile;
        }

        const { error } = await supabase
          .from('organizations')
          .update(updatePayload)
          .eq('id', organizationId);

        if (error) {
          handleSupabaseError(error, 'updateOnboardingData');
        }

        // Return the updated data
        return {
          ...stepData,
          onboarding_meta: updatedMeta
        };
      });

      return { data: result, error: null };
    } catch (error) {
      console.error('Error in updateOnboardingData:', error);
      return {
        data: null,
        error: error instanceof Error ? error : new Error('Unknown error occurred')
      };
    }
  },

  /**
   * Update only the navigation step (for next/previous navigation)
   */
  async updateNavigationStep(
    organizationId: string,
    newStep: number,
    completedSteps: number[]
  ): Promise<OnboardingServiceResponse<OnboardingMeta>> {
    try {
      // Input validation
      if (!organizationId) {
        throw new ValidationError('Organization ID is required');
      }
      
      if (newStep < 1) {
        throw new ValidationError(`Invalid step: ${newStep}. Must be at least 1`);
      }

      const result = await withRetry(async () => {
        const updatedMeta: OnboardingMeta = {
          completed: false,
          current_step: newStep,
          completed_steps: completedSteps
        };

        const { error } = await supabase
          .from('organizations')
          .update({ onboarding_meta: updatedMeta })
          .eq('id', organizationId);

        if (error) {
          handleSupabaseError(error, 'updateNavigationStep');
        }

        return updatedMeta;
      });

      return { data: result, error: null };
    } catch (error) {
      console.error('Error in updateNavigationStep:', error);
      return {
        data: null,
        error: error instanceof Error ? error : new Error('Unknown error occurred')
      };
    }
  },

  /**
   * Complete the onboarding process
   */
  async completeOnboarding(
    organizationId: string,
    totalSteps: number
  ): Promise<OnboardingServiceResponse<OnboardingMeta>> {
    try {
      // Input validation
      if (!organizationId) {
        throw new ValidationError('Organization ID is required');
      }
      
      if (totalSteps < 1) {
        throw new ValidationError(`Invalid total steps: ${totalSteps}. Must be at least 1`);
      }

      const result = await withRetry(async () => {
        const completedMeta: OnboardingMeta = {
          completed: true,
          current_step: totalSteps,
          completed_steps: Array.from({ length: totalSteps }, (_, i) => i + 1),
          completed_at: new Date().toISOString()
        };

        const { error } = await supabase
          .from('organizations')
          .update({ onboarding_meta: completedMeta })
          .eq('id', organizationId);

        if (error) {
          handleSupabaseError(error, 'completeOnboarding');
        }

        return completedMeta;
      });

      return { data: result, error: null };
    } catch (error) {
      console.error('Error in completeOnboarding:', error);
      return {
        data: null,
        error: error instanceof Error ? error : new Error('Unknown error occurred')
      };
    }
  },

  /**
   * Reset onboarding to step 1 (for error recovery)
   */
  async resetOnboarding(organizationId: string): Promise<OnboardingServiceResponse<OnboardingMeta>> {
    try {
      // Input validation
      if (!organizationId) {
        throw new ValidationError('Organization ID is required');
      }

      const result = await withRetry(async () => {
        const resetMeta: OnboardingMeta = {
          completed: false,
          current_step: 1,
          completed_steps: []
        };

        const { error } = await supabase
          .from('organizations')
          .update({ onboarding_meta: resetMeta })
          .eq('id', organizationId);

        if (error) {
          handleSupabaseError(error, 'resetOnboarding');
        }

        return resetMeta;
      });

      return { data: result, error: null };
    } catch (error) {
      console.error('Error in resetOnboarding:', error);
      return {
        data: null,
        error: error instanceof Error ? error : new Error('Unknown error occurred')
      };
    }
  }
}; 